import {UI} from "../../../../../stem-core/src/ui/UIBase";

import {BasePanelPage, BasePanelPageStyle} from "../BasePanelPage";
import {styleRule} from "../../../../../stem-core/src/decorators/Style";
import {getWindowWidth, isSmallScreen, NOOP_FUNCTION} from "../../../../UtilsLib";
import {merchantStyleRule} from "../../../Security";
import {iFrameState} from "../../../../services/IFrameState";
import {registerStyle} from "../../../../../stem-core/src/ui/style/Theme";
import {SubscriptionCoverageCard} from "./components/SubscriptionCoverageCard";
import {iFrameMerchantService} from "../../../../services/IframeMerchantService";
import {subscribeFlowState} from "../../../inline-subscription/SubscribeFlowState";
import {DiscountCodeInput} from "./components/DiscountCodeInput";
import {SOUTHERN_THING_ALIAS, TNR_ALIAS} from "../../../../../blink-sdk/Constants";
import {LabeledCheckbox} from "../../../../ui/Checkbox";
import {MerchantCheckbox} from "../../../../widget/ui/MerchantCheckbox";
import {Messages} from "../../../../Messages";
import {PoweredByBlink} from "../../../PoweredByBlink";
import {authService} from "../../../../../client/connection/Services";
import {Router} from "../../../../../stem-core/src/ui/Router";
import {SIGNUP_EXTERNAL_URL} from "../../../../Constants";
import {UserAddressStore} from "../../../../../client/state/UserAddressStore";
import {SUBSCRIBE_PLAN_STATE, subscribePlanController} from "../../../inline-subscription/SubscribePlanController";
import {CoverageChangeType} from "../../../../../client/state/SubscriptionCoverageStore";
import {SubscriptionApplyPolicy} from "../../../../../client/state/SubscriptionStore";
import {TNRSubscriptionCoverageCard} from "./components/TNRSubscriptionCoverageCard";
import {SubscriptionOfferStore} from "../../../../../client/state/SubscriptionOfferStore";
import {iFrameUserDataService} from "../../../../services/IFrameUserDataService";
import {AnalyticsEventType, dispatchAnalyticsEvent} from "../../../../../blink-sdk/utils/AnalyticsClient";


function getSubscribePlanMaxWidth() {
    return iFrameMerchantService.isMerchant(TNR_ALIAS) ? 936 : 1272;
}

class SubscribePlanPanelStyle extends BasePanelPageStyle {
    @styleRule
    coveragesList = {
        marginLeft: "auto",
        marginRight: "auto",
        marginTop: 24,
        maxWidth: "100%",
    };

    @styleRule
    extraPadding = {
        paddingLeft: () => (isSmallScreen() ? 0 : 144),
        paddingRight: () => (isSmallScreen() ? 0 : 144),
    };

    @styleRule
    coveragesRow = {
        width: "100%",
        display: "flex",
        flexDirection: "row",
        justifyContent: "center",
        flexWrap: () => getWindowWidth() < getSubscribePlanMaxWidth() ? "wrap" : "",
    };

    @styleRule
    coverageCardContainer = {
        paddingBottom: 24,
        ">*": {
            height: "100%",
        },
        paddingRight: isSmallScreen() ? 0 : 12,
        paddingLeft: isSmallScreen() ? 0 : 12,
    };

    @styleRule
    discountCodeInput = {
        marginBottom: 24,
    };

    @styleRule
    shareNameCheckbox = {
        fontSize: () => this.themeProps.MERCHANT_FONT_SIZE_LARGE + " !important",
        fontWeight: this.themeProps.MERCHANT_FONT_WEIGHT_LIGHT,
        margin: "0 auto 12px",
        maxWidth: 524,
        display: "flex",
    };

    messageObject = {
        color: this.themeProps.MERCHANT_TEXT_SECONDARY_COLOR,
        textAlign: "center",
        marginBottom: () => (isSmallScreen() ? 18 : 24),
        width: "100%",
        ":last-child": {
            marginBottom: 0,
        },
    };

    @styleRule
    message = {
        ...this.messageObject,
        fontSize: this.themeProps.MERCHANT_FONT_SIZE_LARGE,
    };

    @merchantStyleRule
    pgLogoStyle = {
        ...iFrameState.logoStyle,
    };

    @merchantStyleRule
    pgTextStyle = {
        ...iFrameState.textStyle,
    };

    @merchantStyleRule
    pgFooterSeparatorStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        ...iFrameState.footerSeparatorStyle,
    };

    @merchantStyleRule
    pgFooterTextStyle = {
        marginLeft: "auto",
        marginRight: "auto",
        ...iFrameState.footerTextStyle,
    };
}

@registerStyle(SubscribePlanPanelStyle)
export class SubscribePlanPanel extends BasePanelPage {
    widthValue = "";
    analyticsDispatched = false;

    extraNodeAttributes(attr) {
        super.extraNodeAttributes(attr);
        attr.setStyle("maxWidth", Math.min(iFrameState.screen.width, getSubscribePlanMaxWidth()));
        attr.setStyle("width", "max-content");
    }

    getAvailableSubscriptionCoveragesListElement() {
        const {styleSheet} = this;
        // TODO: Have a special ordering field instead of "id".
        const subscriptionCoverages = SubscriptionOfferStore.all()
            .map(offer => offer.getCoverage())
            .filter((coverage, index, arr) => arr.indexOf(coverage) === index) // ensure unique coverages are returned
            .sort((coverage1, coverage2) => coverage1.id - coverage2.id);

        if (!this.analyticsDispatched) {
            if (iFrameMerchantService.isMerchant(TNR_ALIAS)) {
                dispatchAnalyticsEvent(AnalyticsEventType.SHOW_SUBSCRIPTION_COVERAGE_CARDS, {
                    coverageCards: SubscriptionOfferStore.all().map(offer => ({
                        coverageId: offer.getCoverage().id,
                        offerIds: [offer.id],
                    }))
                });
            } else {
                dispatchAnalyticsEvent(AnalyticsEventType.SHOW_SUBSCRIPTION_COVERAGE_CARDS, {
                    coverageCards: subscriptionCoverages.map(coverage => ({
                        coverageId: coverage.id,
                        offerIds: coverage.getAvailableOffers().map(offer => offer.id),
                    }))
                });
            }
            this.analyticsDispatched = true;
        }

        let subscriptionCoverageCards = iFrameMerchantService.isMerchant(TNR_ALIAS) ?
            SubscriptionOfferStore.all().map((offer, index) =>
                <TNRSubscriptionCoverageCard offer={offer} bestValue={index === SubscriptionOfferStore.all().length - 1}
                                             onSubscriptionOfferSelected={(offer) => this.handleSubscribeClick(offer)}/>
            ) : subscriptionCoverages.map(coverage =>
                <SubscriptionCoverageCard coverage={coverage}
                                          onSubscriptionOfferSelected={(offer) => this.handleSubscribeClick(offer)}/>);
        subscriptionCoverageCards = subscriptionCoverageCards.map(card =>
            <div className={styleSheet.coverageCardContainer}>{card}</div>);

        if (subscriptionCoverages?.length === 0) {
            return [];
        }

        let coveragesRows = [];
        if (subscriptionCoverageCards.length === 4) {
            coveragesRows.push(...[
                <div className={styleSheet.coveragesRow}>{subscriptionCoverageCards.splice(0, 2)}</div>,
                <div className={styleSheet.coveragesRow}>{subscriptionCoverageCards.splice(0, 2)}</div>
            ]);
        } else {
            coveragesRows = [<div className={styleSheet.coveragesRow}>{subscriptionCoverageCards}</div>]
        }

        const addListExtraPadding = subscriptionCoverageCards.length === 4 || subscriptionCoverageCards.length < 3;
        return <div className={styleSheet.coveragesList + (addListExtraPadding ? styleSheet.extraPadding : "")}>
            {coveragesRows}
        </div>
    }

    render() {
        const {styleSheet} = this;
        const merchant = iFrameMerchantService.getMerchant();
        const {panelText, footerText} = iFrameState;
        const currentSubscription = iFrameUserDataService.getActiveSubscription();
        let subscriptionText;
        switch(subscribePlanController.subscribePlanState ) {
            case SUBSCRIBE_PLAN_STATE.subscribe:
                subscriptionText = panelText ? <div className={styleSheet.message + styleSheet.pgTextStyle}>
                    {panelText.split("\n").map(paragraph => <div>{paragraph || " "}</div>)}
                </div> : null;
                break;
            case SUBSCRIBE_PLAN_STATE.editSubscription:
                subscriptionText = iFrameMerchantService.isMerchant(TNR_ALIAS) ? null :
                    <div className={styleSheet.message + styleSheet.pgTextStyle}>
                        {Messages.currentSubscriptionPlan(currentSubscription.coverage.name, currentSubscription.billingPlan.getCycleDuration(true))}
                    </div>;
                break;
            case SUBSCRIBE_PLAN_STATE.giftSubscription:
                subscriptionText = panelText ? <div className={styleSheet.message + styleSheet.pgTextStyle}>
                    {panelText.split("\n").map(paragraph => <div>{paragraph || " "}</div>)}
                </div> : null;
        }

        return [
            subscriptionText,
            this.getAvailableSubscriptionCoveragesListElement(),
            subscribeFlowState.hasDiscountCodeSupport() ?
                <DiscountCodeInput className={styleSheet.discountCodeInput}
                                   initialValue={this.isDiscountApplied() ? subscribePlanController.discountCode : ""}
                                   discountApplied={this.isDiscountApplied()}
                                   onCodeInput={code => this.checkDiscountCode(code)}
                />
                : null,
            iFrameMerchantService.isMerchant(SOUTHERN_THING_ALIAS)
            && subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.subscribe ?
                <LabeledCheckbox ref="shareNameCheckbox" testId={"shareNameCheckbox"}
                                 className={styleSheet.shareNameCheckbox}
                                 initialValue={subscribePlanController.shareName}
                                 CheckboxClass={MerchantCheckbox}
                                 onChange={enabled => this.handleShareNameCheckboxToggle(enabled)}
                                 label={Messages.subscribeShareUserName(merchant.getName())}
                /> : null,
            <div className={styleSheet.pgFooterSeparatorStyle}/>,
            footerText ? <div className={styleSheet.message + styleSheet.pgFooterTextStyle}>
                {footerText.split("\n").map(paragraph => <div>{paragraph || " "}</div>)}
            </div> : null,
            subscribePlanController.onSelectCancel === NOOP_FUNCTION ? null :
            <div className={styleSheet.center}>
                <div onClick={() => subscribePlanController.onSelectCancel()} className={styleSheet.secondaryButton}>
                    {Messages.back}
                </div>
            </div>,
            subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.subscribe
                && !iFrameMerchantService.isMerchant(TNR_ALIAS) ? <PoweredByBlink/> : null,
        ]
    }

    isDiscountApplied() {
        return !!subscribePlanController.discountCode;
    }

    async checkDiscountCode(code) {
        subscribePlanController.update({discountCode: code});
        if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
            await subscribeFlowState.fetchSubscriptionEditPrices();
        } else {
            await subscribeFlowState.checkDiscountCode();
        }
        this.redraw();
    }

    handleSubscribeClick(offer) {
        subscribePlanController.update({selectedOffer: offer});
        if (!authService.isAuthenticated()) {
            Router.changeURL(SIGNUP_EXTERNAL_URL);
            return;
        }
        if (subscribePlanController.subscribePlanState === SUBSCRIBE_PLAN_STATE.editSubscription) {
            const instantPayInfo = subscribeFlowState.getDiscountInfoOnInstantChangePolicy(offer);

            subscribePlanController.update({
                applyPolicy: instantPayInfo?.coverageChangeType == CoverageChangeType.UPGRADE ?
                    SubscriptionApplyPolicy.INSTANTLY : SubscriptionApplyPolicy.NEXT_BILLING_CYCLE
            });
        }
        subscribePlanController.onSelectSuccess();
    }

    handleShareNameCheckboxToggle(enabled) {
        subscribePlanController.update({shareName: enabled});
        this.redraw();
    }

    async onMount() {
        super.onMount();

        this.attachUpdateListener(iFrameState, () => this.redraw());
        this.attachChangeListener(UserAddressStore, () => this.redraw());

        // TODO: This should just disappear. But at this point I am afraid to remove it right now (I am 2 hours behind a release rn).
        this.attachListener(subscribeFlowState, "requestsFinished", () => {
            this.redraw();
        });
    }
}
